import type { ReactNode } from "react";
import { FiligreeFrame } from "@/components/brand/FiligreeFrame";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { Seal } from "@/components/brand/Seal";

interface BrandedErrorLayoutProps {
  eyebrow: string;
  sealTitle: string;
  heading: ReactNode;
  body: ReactNode;
  children?: ReactNode;
  footnote?: ReactNode;
}

/**
 * Shared editorial layout for 404, 500, and maintenance pages.
 * Cream parchment background, white card framed in filigree, navy seal up top.
 */
export function BrandedErrorLayout({
  eyebrow,
  sealTitle,
  heading,
  body,
  children,
  footnote,
}: BrandedErrorLayoutProps) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-cream-50 px-5 py-24 md:py-32">
      <div className="w-full max-w-[720px]">
        <FiligreeFrame intensity="medium" className="bg-navy-800/[0.02]">
          <div
            className="relative bg-white px-6 py-12 text-center md:px-12 md:py-14"
            style={{
              borderRadius: "4px",
              boxShadow: "0 20px 60px -20px rgba(168,139,92,0.25)",
            }}
          >
            <div className="flex justify-center">
              <Seal title={sealTitle} size="sm" />
            </div>
            <div className="mt-6 flex justify-center">
              <Eyebrow>{eyebrow}</Eyebrow>
            </div>
            <h1 className="mt-4 font-display text-[32px] leading-tight text-navy-800 md:text-[40px]">
              {heading}
            </h1>
            <p className="mx-auto mt-5 max-w-[560px] font-sans text-[15px] leading-[1.7] text-charcoal-500">
              {body}
            </p>
            {children && <div className="mt-8">{children}</div>}
            {footnote && (
              <p className="mt-6 font-sans text-[13px] text-charcoal-500">{footnote}</p>
            )}
          </div>
        </FiligreeFrame>
      </div>
    </main>
  );
}